"use client";
import { useState, useEffect, useCallback, memo } from "react";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import { usePathname } from "next/navigation";

import logoIcon from "@public/logo.svg";
import arrowOut from "@public/arrow_out.svg";
import homeIcon from "@public/home_icon.png";
import learnIcon from "@public/research.png";

import { PATHS } from "@/shared/utils/paths";
import Logo from "@/shared/components/Logo";
import { scheduleIdleTask } from "@/shared/utils/scheduleIdleTask";

import s from "./styles.module.css";

interface BurgerIconProps {
  isOpened: boolean;
  onClick: () => void;
}

function BurgerIcon({ isOpened, onClick }: BurgerIconProps) {
  return (
    <button
      className={clsx(s.burger, isOpened && s.open)}
      onClick={onClick}
      aria-label={isOpened ? "Close menu" : "Open menu"}
    >
      <svg
        width="30"
        height="30"
        viewBox="0 0 30 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          className={s.line1}
          width="24"
          height="2"
          transform="translate(3 10)"
          fill="white"
        />
        <rect
          className={s.line2}
          width="24"
          height="2"
          transform="translate(3 18)"
          fill="white"
        />
      </svg>
    </button>
  );
}

const MemoizedBurgerIcon = memo(BurgerIcon);

interface MobileLinkProps {
  children: React.ReactNode;
  href: string;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const MobileLink = ({ children, href, onClick }: MobileLinkProps) => {
  return (
    <Link href={href} onClick={onClick} className={s.mobileLink}>
      <div className={s.mobileLinkContent}>{children}</div>
      <Image
        src={arrowOut}
        alt="arrow icon for external link"
        className={s.arrowOut}
      />
    </Link>
  );
};

export default function Header() {
  const t = useTranslations("Header");

  const pathname = usePathname();

  const [isOpened, setIsOpened] = useState(false);

  const handleIdle = useCallback(() => setIsOpened(false), []);

  useEffect(() => {
    setTimeout(() => {
      scheduleIdleTask(handleIdle);
    }, 20);
  }, [pathname, handleIdle]);

  const prevent = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      if (pathname === href) {
        e.preventDefault();
        setIsOpened(false);
      }
    },
    [pathname]
  );

  const onClickBurger = useCallback(() => {
    setIsOpened((state) => !state);
  }, []);

  return (
    <header className={clsx(s.container, isOpened && s.containerOpened)}>
      <Link className={s.homeLink} href={PATHS.home}>
        <Image src={logoIcon} alt="Sweat official logo" />
      </Link>
      <nav className={s.desktopNav}>
        <Link
          onClick={(e) => prevent(e, PATHS.token)}
          className={s.link}
          href={PATHS.token}
        >
          {t("token")}
        </Link>
        <Link
          onClick={(e) => prevent(e, PATHS.wallet)}
          className={s.link}
          href={PATHS.wallet}
        >
          {t("wallet")}
        </Link>
        <Link
          onClick={(e) => prevent(e, PATHS.learn)}
          className={s.link}
          href={PATHS.learn}
        >
          {t("learn")}
        </Link>
        <Link onClick={(e) => prevent(e, PATHS.blog)} className={s.link} href={PATHS.blog}>
          {t("blog")}
        </Link>
        <Link
          onClick={(e) => prevent(e, PATHS.partners)}
          className={s.link}
          href={PATHS.partners}
        >
          {t("partners")}
        </Link>
        {/*
        <Link
          onClick={(e) => prevent(e, PATHS.about)}
          className={s.link}
          href={PATHS.about}
        >
          {t("about")}
        </Link>
        <Link
          onClick={(e) => prevent(e, PATHS.more)}
          className={s.link}
          href={PATHS.more}
        >
          {t("more")}
        </Link>
        */}
      </nav>
      <MemoizedBurgerIcon isOpened={isOpened} onClick={onClickBurger} />
      <div className={clsx(s.mobileMenu, isOpened && s.mobileMenuOpened)}>
        <MobileLink onClick={(e) => prevent(e, PATHS.token)} href={PATHS.token}>
          <Logo type="round" className={s.mobileLinkLogo} />
          {t("token")}
        </MobileLink>
        <MobileLink
          onClick={(e) => prevent(e, PATHS.wallet)}
          href={PATHS.wallet}
        >
          <Logo type="square" className={s.mobileLinkLogo} />
          {t("wallet")}
        </MobileLink>
        <MobileLink onClick={(e) => prevent(e, PATHS.learn)} href={PATHS.learn}>
          <Image src={learnIcon} className={s.homeIcon} alt="Learn icon" />
          {t("learn")}
        </MobileLink>
        <MobileLink onClick={(e) => prevent(e, PATHS.blog)} href={PATHS.blog}>
          <Image src={learnIcon} className={s.homeIcon} alt="" />
          {t("blog")}
        </MobileLink>
        <MobileLink onClick={(e) => prevent(e, PATHS.home)} href={PATHS.home}>
          <Image src={homeIcon} className={s.homeIcon} alt="home icon" />
          {t("home")}
        </MobileLink>
      </div>
    </header>
  );
}
